// ─────────────────────────────────────────────
// errorTracker.ts
// Single source of truth for sending errors to backend.
// All calls should go through logError() in app.ts,
// NOT directly through this function (to keep guard logic central).
// ─────────────────────────────────────────────

export type ErrorType =
    | "vue_error"
    | "api_error"
    | "network_error"
    | "unhandled_rejection"
    | "global_error"
    | "slow_api"
    | "resource_error";

export interface FrontendErrorPayload {
    type: ErrorType;
    message: string;
    url?: string;
    method?: string;
    status?: number;
    stack?: string;
    component?: string;
    info?: string;
    context?: Record<string, unknown>;
}

const ENDPOINT = "/api/frontend-error";

/** Read Laravel's XSRF-TOKEN cookie (set by the web middleware, readable by JS) */
const getXsrfToken = (): string | null => {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN\s*=\s*([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
};

const sendFrontendError = (payload: FrontendErrorPayload): void => {
    // Guard: never call the error endpoint recursively
    if (payload.url?.includes(ENDPOINT)) return;

    try {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
        };
        // Laravel's CSRF middleware checks X-XSRF-TOKEN for non-GET requests.
        // fetch() bypasses axios interceptors so we must attach it manually.
        const xsrf = getXsrfToken();
        if (xsrf) headers["X-XSRF-TOKEN"] = xsrf;

        void fetch(ENDPOINT, {
            method: "POST",
            // keepalive: true ensures the request completes even if page unloads
            keepalive: true,
            headers,
            body: JSON.stringify({
                ...payload,
                // Enrich with user & browser context at send time
                user_id: (window as any).App?.user?.id ?? null,
                user_agent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                // NOTE: page/url/query are passed via payload.context from app.ts
                // Do NOT add window.location here — it would duplicate those fields
            }),
        }).catch((error) => {
            // Silent fail — tracker transport errors should never break app flow
            console.warn("[errorTracker] Transport failed:", error);
        });
    } catch (e) {
        // Silent fail — never throw from the error tracker itself
        console.warn("[errorTracker] Failed to send error:", e);
    }
};

export default sendFrontendError;